import {
    CheckCircle2,
    Circle,
    Package,
    PlusCircle,
    MinusCircle,
    RefreshCw,
    Tag,
    Truck,
    AlertCircle,
    ShoppingCart
} from 'lucide-react'
import { OrderModel } from '@/types/model/Order'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'

interface OrderTimelineProps {
    logs: OrderModel.OrderLog[]
}

export const OrderTimeline = ({ logs }: OrderTimelineProps) => {
    const getLogIcon = (action: string) => {
        const lowerAction = action.toLowerCase()
        if (lowerAction.includes('placed')) return ShoppingCart
        if (lowerAction.includes('accepted') || lowerAction.includes('processed')) return CheckCircle2
        if (lowerAction.includes('added')) return PlusCircle
        if (lowerAction.includes('removed')) return MinusCircle
        if (lowerAction.includes('updated') || lowerAction.includes('quantity')) return RefreshCw
        if (lowerAction.includes('discount')) return Tag
        if (lowerAction.includes('shipped') || lowerAction.includes('dispatch') || lowerAction.includes('way')) return Truck
        if (lowerAction.includes('delivered')) return Package
        if (lowerAction.includes('cancel')) return AlertCircle
        return Circle
    }

    const getLogColor = (action: string) => {
        const lowerAction = action.toLowerCase()
        if (lowerAction.includes('placed')) return 'text-info-dark bg-info-lighter'
        if (lowerAction.includes('accepted') || lowerAction.includes('processed')) return 'text-success-dark bg-success-lighter'
        if (lowerAction.includes('removed')) return 'text-error-dark bg-error-lighter'
        if (lowerAction.includes('updated') || lowerAction.includes('quantity')) return 'text-warning-dark bg-warning-lighter'
        if (lowerAction.includes('delivered')) return 'text-success-dark bg-success-lighter'
        if (lowerAction.includes('cancel')) return 'text-error-dark bg-error-lighter'
        return 'text-text-2 bg-muted'
    }

    // Sort logs by date descending (latest first)
    const sortedLogs = [...(logs || [])].sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )

    return (
        <div className="p-4">
            {sortedLogs.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                    No history yet for this order.
                </p>
            ) : (
                <ol className="relative space-y-5 before:absolute before:bottom-2 before:left-3.5 before:top-2 before:w-px before:bg-border">
                    {sortedLogs.map((log, index) => {
                        const Icon = getLogIcon(log.action)
                        const colorClass = getLogColor(log.action)

                        return (
                            <li key={log._id || index} className="relative flex items-start gap-3">
                                <div className={cn(
                                    'relative z-10 flex size-7 shrink-0 items-center justify-center rounded-full ring-4 ring-card',
                                    colorClass
                                )}>
                                    <Icon className="size-3.5" />
                                </div>

                                <div className="flex min-w-0 flex-1 flex-col gap-0.5 pt-1">
                                    <div className="flex flex-col justify-between gap-1 sm:flex-row sm:items-center">
                                        <p className="text-sm font-medium text-ink">{log.action}</p>
                                        {log.created_at && (
                                            <time className="shrink-0 text-xs tabular-nums text-muted-foreground">
                                                {format(new Date(log.created_at || ''), 'MMM d, yyyy • h:mm a')}
                                            </time>
                                        )}
                                    </div>
                                    {log.action_type && (
                                        <p className="text-xs capitalize text-muted-foreground">
                                            {log.action_type.replace(/_/g, ' ')}
                                        </p>
                                    )}
                                </div>
                            </li>
                        )
                    })}
                </ol>
            )}
        </div>
    )
}
